import {
  FOC_TOUR_LABEL_INCIDENTS,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  IOC_CARRYOVER_DISPOSITION,
} from '../orgWorkspaceTour/orgWorkspaceTourSteps';
import { CorporateViewTabs } from '../hubNavTabs';

export const FOC_INCIDENTS_TOUR_PARAM = 'focIncidentsTour';

/** Opens Hub Incidents and starts the 7.1 Incident Management walkthrough. */
export const FOC_INCIDENTS_TOUR_HREF = `/hub?openTab=${CorporateViewTabs.Incidents}&${FOC_INCIDENTS_TOUR_PARAM}=1`;

/** Create Incident wizard surface for FOC-INC create steps. */
export const FOC_INCIDENTS_TOUR_WIZARD_HREF = `/create/incident?${FOC_INCIDENTS_TOUR_PARAM}=1`;

export type FocIncidentsTourSurface =
  | 'incidents'
  | 'createMenu'
  | 'createWizard';

export type FocIncidentsTourStepId =
  | 'sectionIntro'
  | 'usi001'
  | 'usi003'
  | 'usi008'
  | 'usi005'
  | 'usi006'
  | 'usi007'
  | 'inc001'
  | 'inc003'
  | 'inc003a'
  | 'inc002'
  | 'inc002a'
  | 'inc002b'
  | 'inc004'
  | 'inc004a'
  | 'inc005'
  | 'focFeedback';

export type FocIncidentsTourStepDef = {
  id: FocIncidentsTourStepId;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  tour?: string;
  surface?: FocIncidentsTourSurface;
};

type RequirementInput = Omit<
  FocIncidentsTourStepDef,
  'title' | 'requirementId'
> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocIncidentsTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

/**
 * Walk for 7.1 Incident Management: US-I001–I008 (carryover) and FOC-INC-001–005,
 * ending with FOC Feedback Notes.
 */
export const FOC_INCIDENTS_TOUR_STEPS: FocIncidentsTourStepDef[] = [
  {
    id: 'sectionIntro',
    surface: 'incidents',
    tour: 'incidents-workspace',
    title: '7.1 Incident Management',
    description:
      'View and manage incidents from the Hub Incidents tab, then walk the Create Incident workflow used to stand up Coast Guard and joint incident workspaces.',
  },
  requirement({
    id: 'usi001',
    requirementId: 'US-I001',
    name: 'Access Incidents Tab',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'incidents',
    tour: 'hub-tab-incidents',
    description:
      'Select Incidents from Hub navigation (under More) so you can view and manage incident information.',
  }),
  requirement({
    id: 'usi003',
    requirementId: 'US-I003',
    name: 'View Incident Summary Information',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'incidents',
    tour: 'incident-summary',
    description:
      'In the collapsed card, see key summary details — incident type, date, and area of responsibility — so you can assess priority without expanding. (Severity and status badges are not shown on the collapsed row today.)',
  }),
  requirement({
    id: 'usi008',
    requirementId: 'US-I008',
    name: 'All Incidents Collapsed by Default',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'incidents',
    tour: 'incidents-list',
    description:
      'When you open the Incidents tab, all incidents start collapsed so you see a clean overview list.',
  }),
  requirement({
    id: 'usi005',
    requirementId: 'US-I005',
    name: 'Expand Individual Incident',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'incidents',
    tour: 'incident-expand',
    description:
      'Expand an incident to see detailed information so you can understand the full situation.',
  }),
  requirement({
    id: 'usi006',
    requirementId: 'US-I006',
    name: 'View Detailed Incident Information',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'incidents',
    tour: 'incident-detail',
    description:
      'When expanded, review comprehensive details including categories, affected area, related events, operational period, and status context.',
  }),
  requirement({
    id: 'usi007',
    requirementId: 'US-I007',
    name: 'Collapse Expanded Incident',
    disposition: IOC_CARRYOVER_DISPOSITION,
    surface: 'incidents',
    tour: 'incident-expand',
    description:
      'Collapse an expanded incident to reduce clutter and focus on other items.',
  }),
  requirement({
    id: 'inc001',
    requirementId: 'FOC-INC-001',
    name: 'Quick-Action Landing Page',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'createMenu',
    tour: 'hub-create-incident',
    description:
      'From the Hub + Create menu, authorized users can start high-confidence actions such as Create Incident. A fuller role-aware landing (Create Exercise, Manage My IMT, administration, Program Management) continues to mature alongside this Create entry point.',
  }),
  requirement({
    id: 'inc003',
    requirementId: 'FOC-INC-003',
    name: 'Initial Incident Naming',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'createWizard',
    tour: 'create-incident-name',
    description:
      'PRATUS supports an initial placeholder incident name and a later authorized update when Incident Command or Unified Command formalizes the incident name.',
  }),
  requirement({
    id: 'inc003a',
    requirementId: 'FOC-INC-003a',
    name: 'Neutral Naming',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'createWizard',
    tour: 'create-incident-name',
    description:
      'The naming workflow supports names that describe the incident without unnecessarily assigning fault, responsibility, or reputational stigma to an event owner or potential responsible party.',
  }),
  requirement({
    id: 'inc002',
    requirementId: 'FOC-INC-002',
    name: 'Coast Guard Incident Context',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'createWizard',
    tour: 'create-incident-category',
    description:
      'During incident creation, capture a mission category or primary mission using Coast Guard-relevant options (for example Search and Rescue, Marine Environmental Response, severe weather, hazardous materials, mass rescue, special event, and other approved mission sets available to your organization).',
  }),
  requirement({
    id: 'inc002a',
    requirementId: 'FOC-INC-002a',
    name: 'Default Coast Guard ICS Context',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'createWizard',
    tour: 'create-incident-ics-family',
    description:
      'For Coast Guard-owned incidents, PRATUS defaults the incident workspace to Coast Guard ICS concepts, terminology, templates, and operational workflows via the ICS Form Set (USCG by default).',
  }),
  requirement({
    id: 'inc002b',
    requirementId: 'FOC-INC-002b',
    name: 'Joint and Partner Incident Context',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'createWizard',
    tour: 'create-incident-ics-family',
    description:
      'PRATUS supports authorized joint, Unified Command, partner-led, or nonstandard incident participation (for example selecting a FEMA form set) without requiring Coast Guard users to abandon Coast Guard incident-management workflow requirements.',
  }),
  requirement({
    id: 'inc004',
    requirementId: 'FOC-INC-004',
    name: 'Incident Location and Area of Responsibility',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'createWizard',
    tour: 'create-incident-location',
    description:
      'Rapidly establish and later update incident location, affected area, point, polygon, geofence, geographic area of responsibility, and related mapping context.',
  }),
  requirement({
    id: 'inc004a',
    requirementId: 'FOC-INC-004a',
    name: 'Intelligent Defaults and User Override',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'createWizard',
    tour: 'create-incident-location',
    description:
      'PRATUS may prepopulate incident fields from authorized organizational context, Area of Responsibility, and available authoritative data. Users with appropriate permissions can override defaults when the incident location, ownership, mission, or affected area differs from the user’s home unit or assigned AOR.',
  }),
  requirement({
    id: 'inc005',
    requirementId: 'FOC-INC-005',
    name: 'Protected Command Decisions',
    disposition: FOC_REQUIRED_DISPOSITION,
    surface: 'createWizard',
    description:
      'PRATUS is required to protect incident name, location, operational period, approved incident objectives, command structure, and other designated command decisions from unauthorized modification, and to record authorized changes with audit information. Dedicated protection UI for these decisions continues to mature beyond this walkthrough.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_INCIDENTS),
  },
];

export function focIncidentsTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focIncidentsTourStepIds(): FocIncidentsTourStepId[] {
  return FOC_INCIDENTS_TOUR_STEPS.map((step) => step.id);
}

export function focIncidentsTourStepTitles(): string[] {
  return FOC_INCIDENTS_TOUR_STEPS.map((step) => step.title);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function focIncidentsTourPopoverHtml(
  step: Pick<
    FocIncidentsTourStepDef,
    'disposition' | 'description' | 'bullets'
  >
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}
